import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import * as api from '../api.ts';
import {
  Button,
  Cell,
  Empty,
  Finding,
  PageTitle,
  Row,
  Section,
  Select,
  Spinner,
  Status,
  Table,
} from '../ui.tsx';
import { SitePicker } from './readiness.tsx';

/**
 * One question: **who else shares this building, and does the escalation
 * path work?**
 *
 * Two halves for the two hats an org can wear (`roster-and-presence.md`):
 * the occupier's — declare which venue this site sits in, so escalation has
 * a relationship to flow through — and the operator's — confirm tenancies
 * and keep the on-call roster staffed, because an escalation policy with an
 * empty roster pages nobody.
 */
export function Venue({
  orgId,
  sites,
  siteId,
  onSite,
}: {
  orgId: string;
  sites: api.Site[];
  siteId: string;
  onSite: (id: string) => void;
}) {
  const qc = useQueryClient();
  const site = sites.find((s) => s.id === siteId);

  const tenancies = useQuery({
    queryKey: ['tenancies', orgId],
    queryFn: () => api.getTenancies(orgId),
  });
  const operated = useQuery({
    queryKey: ['operated-venues', orgId],
    queryFn: () => api.operatedVenues(orgId),
  });
  const nearby = useQuery({
    queryKey: ['venues-nearby', site?.latitude, site?.longitude],
    queryFn: () => api.venuesNearby(site!.latitude!, site!.longitude!),
    enabled: site?.latitude != null && site?.longitude != null,
  });

  const [note, setNote] = useState<string | null>(null);
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['tenancies', orgId] });
  };

  const request = useMutation({
    mutationFn: (venueId: string) => api.requestTenancy(orgId, venueId, siteId),
    onSuccess: (res) => {
      setNote(
        res?.status === 'already_requested'
          ? 'Already requested — the operator has it.'
          : 'Requested. The venue operator confirms it from their side.',
      );
      invalidate();
    },
    onError: (e: Error) => setNote(e.message),
  });

  const confirm = useMutation({
    mutationFn: (tenancyId: string) => api.confirmTenancy(orgId, tenancyId),
    onSuccess: invalidate,
    onError: (e: Error) => setNote(e.message),
  });

  const mine = (tenancies.data?.tenancies ?? []).filter((t) => t.mine);
  const inbound = (tenancies.data?.tenancies ?? []).filter((t) => !t.mine);
  const operatedList = operated.data?.venues ?? [];
  const linkedVenueIds = new Set((tenancies.data?.tenancies ?? []).map((t) => t.venue_id));

  return (
    <>
      <PageTitle sub="Who shares the building, and whether the escalation path works.">
        Venue
      </PageTitle>

      <div className="mb-6">
        <SitePicker sites={sites} siteId={siteId} onChange={onSite} />
      </div>

      {note ? (
        <div className="mb-6">
          <Finding tone="neutral">{note}</Finding>
        </div>
      ) : null}

      <Section title="This site's venue">
        {mine.length > 0 ? (
          <Table head={['Venue', 'Site', 'State']}>
            {mine.map((t) => (
              <Row key={t.id}>
                <Cell wide>{t.venue_name}</Cell>
                <Cell>{t.site_name ?? '—'}</Cell>
                <Cell>
                  {t.state === 'confirmed' ? (
                    <Status tone="safe">Confirmed — escalation available</Status>
                  ) : (
                    <Status tone="attention">Awaiting the operator</Status>
                  )}
                </Cell>
              </Row>
            ))}
          </Table>
        ) : (
          <Empty>
            This site is not linked to a venue. If it sits inside a centre, campus or tower somebody
            else runs, link it — escalation to their control room needs the relationship first.
          </Empty>
        )}

        {site?.latitude == null ? (
          <p className="mt-4 text-[14px] text-[var(--ink-2)]">
            Pin this site on the map (Sites screen) to see the venues around it.
          </p>
        ) : nearby.isLoading ? (
          <Spinner />
        ) : (nearby.data?.venues.length ?? 0) > 0 ? (
          <div className="mt-5 border-t border-[var(--line)] pt-4">
            <p className="mb-3 text-[14px] text-[var(--ink-2)]">Venues near this site:</p>
            <Table head={['Venue', 'Locality', 'Distance', '']}>
              {(nearby.data?.venues ?? []).slice(0, 8).map((v) => (
                <Row key={v.id}>
                  <Cell wide>{v.name}</Cell>
                  <Cell>{v.locality ?? '—'}</Cell>
                  <Cell numeric>{v.distance_km} km</Cell>
                  <Cell>
                    {linkedVenueIds.has(v.id) ? (
                      <span className="text-[13px] text-[var(--ink-2)]">Linked</span>
                    ) : v.claimed ? (
                      <Button
                        variant="quiet"
                        label={`Link this site to ${v.name}`}
                        disabled={request.isPending}
                        onClick={() => request.mutate(v.id)}
                      >
                        Link this site
                      </Button>
                    ) : (
                      <span className="text-[13px] text-[var(--ink-2)]">No operator yet</span>
                    )}
                  </Cell>
                </Row>
              ))}
            </Table>
          </div>
        ) : null}
      </Section>

      {inbound.length > 0 ? (
        <Section title="Tenants asking to link">
          <Table head={['Occupier', 'Venue', 'State', '']}>
            {inbound.map((t) => (
              <Row key={t.id}>
                <Cell wide>{t.occupier_name ?? '—'}</Cell>
                <Cell>{t.venue_name}</Cell>
                <Cell>
                  {t.state === 'confirmed' ? (
                    <Status tone="safe">Confirmed</Status>
                  ) : (
                    <Status tone="attention">Pending</Status>
                  )}
                </Cell>
                <Cell>
                  {t.state === 'pending' ? (
                    <Button
                      variant="primary"
                      disabled={confirm.isPending}
                      label={`Confirm the tenancy for ${t.occupier_name ?? 'this occupier'}`}
                      onClick={() => confirm.mutate(t.id)}
                    >
                      Confirm
                    </Button>
                  ) : null}
                </Cell>
              </Row>
            ))}
          </Table>
        </Section>
      ) : null}

      {operatedList.map((v) => (
        <div key={v.id}>
          <VenueLogo orgId={orgId} venueId={v.id} venueName={v.name} currentUrl={v.logo_url} />
          <Oncall orgId={orgId} venueId={v.id} venueName={v.name} />
        </div>
      ))}
    </>
  );
}

/**
 * The venue's own wordmark, shown beside the name on every Discover
 * row. Wins over the Wikimedia default the resolver falls back to,
 * because a rebrand leaves Wikimedia lagging by months and the org
 * knows what its current mark is.
 *
 * SVG/PNG/JPEG/WEBP, half a megabyte. The endpoint sniffs the mime
 * and refuses an SVG carrying `<script>` or an `on*` handler — the
 * upload path enforces it rather than the CDN header, so a hostile
 * file cannot slip through by being served with a lax content-type.
 */
function VenueLogo({
  orgId,
  venueId,
  venueName,
  currentUrl,
}: {
  orgId: string;
  venueId: string;
  venueName: string;
  currentUrl: string | null;
}) {
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const invalidate = () => void qc.invalidateQueries({ queryKey: ['operated-venues', orgId] });

  const upload = useMutation({
    mutationFn: (file: File) => api.uploadVenueLogo(orgId, venueId, file),
    onSuccess: () => {
      setError(null);
      invalidate();
    },
    onError: (e: Error) => setError(readableUploadError(e)),
  });

  const clear = useMutation({
    mutationFn: () => api.clearVenueLogo(orgId, venueId),
    onSuccess: invalidate,
    onError: (e: Error) => setError(e.message),
  });

  return (
    <Section title={`Logo for ${venueName}`}>
      <div className="flex flex-wrap items-center gap-6">
        <div
          className="flex h-[72px] w-[72px] items-center justify-center overflow-hidden rounded-full border border-[var(--line)] bg-[var(--paper-3)]"
          aria-label={currentUrl ? `${venueName} logo` : 'No logo uploaded'}
        >
          {currentUrl ? (
            <img src={currentUrl} alt="" className="max-h-[52px] max-w-[52px] object-contain" />
          ) : (
            <span className="text-[24px] font-semibold text-[var(--ink-2)]">
              {venueName
                .replace(/^(the|a|an)\s+/i, '')
                .charAt(0)
                .toUpperCase()}
            </span>
          )}
        </div>

        <div className="flex flex-col gap-2">
          <label
            className="inline-flex cursor-pointer items-center gap-2 self-start rounded-full border border-[var(--line-2)] bg-[var(--paper)] px-4 py-2 text-[14px] hover:bg-[var(--paper-3)]"
            aria-label={`Upload a new logo for ${venueName}`}
          >
            <input
              type="file"
              accept="image/svg+xml,image/png,image/jpeg,image/webp"
              className="sr-only"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) upload.mutate(file);
                // Reset so choosing the same file twice re-fires
                // the change handler, otherwise re-uploading the
                // same failed file does nothing.
                e.target.value = '';
              }}
            />
            {upload.isPending ? <Spinner /> : null}
            {currentUrl ? 'Replace logo' : 'Upload a logo'}
          </label>
          {currentUrl ? (
            <Button
              variant="quiet"
              disabled={clear.isPending}
              label={`Remove the uploaded logo for ${venueName}`}
              onClick={() => clear.mutate()}
            >
              Remove
            </Button>
          ) : null}
          <p className="text-[13px] text-[var(--ink-2)]">
            SVG, PNG, JPEG or WEBP. Under 512 KB. Overwrites the Wikimedia default we would
            otherwise use.
          </p>
        </div>
      </div>
      {error ? (
        <div className="mt-4">
          <Finding>{error}</Finding>
        </div>
      ) : null}
    </Section>
  );
}

/**
 * Translate the API's terse error codes into a sentence somebody can
 * act on. `NOT_CONFIGURED` in particular reads as "we did not set up
 * the bucket" and must not surface as jargon on a dashboard.
 */
function readableUploadError(err: Error): string {
  const msg = err.message;
  if (msg === 'MALICIOUS_SVG')
    return 'That SVG contained a script or event handler. Re-export it as a plain SVG or use PNG.';
  if (msg === 'BAD_MIME') return 'Only SVG, PNG, JPEG or WEBP files are accepted.';
  if (msg === 'TOO_LARGE') return 'The file is over 512 KB. Shrink it and try again.';
  if (msg === 'NOT_CONFIGURED')
    return 'Uploads are not enabled on this environment yet. Ask an operator.';
  return msg;
}

/**
 * The on-call roster for a venue this org operates. Position 0 is paged
 * first; an unacknowledged escalation walks down the list and wraps. An
 * empty roster means escalations page nobody — the emptiness is the finding.
 */
function Oncall({
  orgId,
  venueId,
  venueName,
}: {
  orgId: string;
  venueId: string;
  venueName: string;
}) {
  const qc = useQueryClient();
  const oncall = useQuery({
    queryKey: ['oncall', orgId, venueId],
    queryFn: () => api.getOncall(orgId, venueId),
  });
  const roster = useQuery({ queryKey: ['roster', orgId], queryFn: () => api.getRoster(orgId) });
  const [userId, setUserId] = useState('');
  const [error, setError] = useState<string | null>(null);

  const invalidate = () => void qc.invalidateQueries({ queryKey: ['oncall', orgId, venueId] });

  const add = useMutation({
    mutationFn: () => {
      const taken = new Set((oncall.data?.oncall ?? []).map((o) => o.position));
      let position = 0;
      while (taken.has(position)) position += 1;
      return api.addOncall(orgId, venueId, userId, position);
    },
    onSuccess: () => {
      setUserId('');
      setError(null);
      invalidate();
    },
    onError: (e: Error) => setError(e.message),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.removeOncall(orgId, venueId, id),
    onSuccess: invalidate,
  });

  const people = (roster.data?.members ?? []).filter((m) => !m.deprovisioned_at);
  const entries = oncall.data?.oncall ?? [];

  return (
    <Section title={`On call for ${venueName}`}>
      {entries.length === 0 ? (
        <Finding>
          <strong>Nobody is on call.</strong> An escalation from a tenant would page nobody — this
          list is what makes the acknowledgement promise real.
        </Finding>
      ) : (
        <Table head={['Order', 'Person', '']}>
          {entries.map((o) => (
            <Row key={o.id}>
              <Cell numeric>{o.position === 0 ? 'First' : o.position + 1}</Cell>
              <Cell wide>{o.name}</Cell>
              <Cell>
                <Button
                  variant="quiet"
                  label={`Take ${o.name} off the on-call roster`}
                  onClick={() => remove.mutate(o.id)}
                >
                  Remove
                </Button>
              </Cell>
            </Row>
          ))}
        </Table>
      )}

      <div className="mt-4 flex flex-wrap items-end gap-3">
        <div className="w-[260px]">
          <Select
            label="Add to your people"
            value={userId}
            onChange={setUserId}
            options={[
              { value: '', label: 'Choose someone…' },
              ...people.map((m) => ({
                value: m.user_id,
                label: m.display_name ?? m.email ?? 'Unnamed',
              })),
            ]}
          />
        </div>
        <Button
          variant="secondary"
          disabled={!userId || add.isPending}
          onClick={() => add.mutate()}
        >
          Add
        </Button>
      </div>
      {error ? (
        <div className="mt-3">
          <Finding>{error}</Finding>
        </div>
      ) : null}
    </Section>
  );
}
